/**
 * Vue2 Web SpeechSynthesis 带队列语音播报工具
 * 注意：浏览器安全限制：第一次发声必须由用户click交互触发
 */

let synth = window.speechSynthesis
let voiceList = []

// 全局状态
export const voiceState = {
  enableVoice: false, // 总开关：是否开启语音播报
  isSpeaking: false, // 是否正在播报
}

// 播报任务队列
let speakQueue = []

// 初始化：从localStorage恢复开关状态
const localVoice = localStorage.getItem('voiceSwitch')
if (localVoice === 'true') {
  voiceState.enableVoice = true
}

// 加载系统语音列表
function loadVoices() {
  voiceList = synth.getVoices()
}

// 监听语音列表变化（部分浏览器异步才拿到语音）
if (synth) {
  loadVoices()
  synth.onvoiceschanged = loadVoices
}

/**
 * 从队列取出下一条执行播报
 */
function runNextTask() {
  if (!voiceState.enableVoice) {
    speakQueue = []
    voiceState.isSpeaking = false
    return
  }
  if (voiceState.isSpeaking) return
  if (speakQueue.length === 0) {
    voiceState.isSpeaking = false
    return
  }

  voiceState.isSpeaking = true
  const task = speakQueue.shift()
  const { text, opts, resolve, reject } = task

  try {
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'zh‑CN'
    utterance.rate = opts.rate ?? 1
    utterance.pitch = opts.pitch ?? 1
    utterance.volume = opts.volume ?? 1

    // 优先匹配中文人声
    const zhVoice = voiceList.find(v => /zh/i.test(v.lang))
    if (zhVoice) utterance.voice = zhVoice

    utterance.onend = () => {
      voiceState.isSpeaking = false
      resolve({ ok: true, msg: '播报结束' })
      runNextTask()
    }

    utterance.onerror = (err) => {
      voiceState.isSpeaking = false
      reject(err)
      runNextTask()
    }

    synth.speak(utterance)
  } catch (e) {
    voiceState.isSpeaking = false
    reject(e)
    runNextTask()
  }
}

/**
 * 加入播报队列
 * @param {string} text 播报文本
 * @param {Object} opts {rate,pitch,volume}
 * @returns Promise
 */
export function speakEnqueue(text, opts = {}) {
  if (!synth) {
    return Promise.reject(new Error('当前环境不支持 speechSynthesis'))
  }
  if (!voiceState.enableVoice) {
    return Promise.reject(new Error('语音播报未开启'))
  }
  if (!text || String(text).trim() === '') {
    return Promise.reject(new Error('播报文本为空'))
  }

  return new Promise((resolve, reject) => {
    speakQueue.push({
      text: String(text).trim(),
      opts,
      resolve,
      reject
    })
    runNextTask()
  })
}

/**
 * 停止播报，清空队列
 */
export function stopAllSpeak() {
  if (synth) {
    synth.cancel()
  }
  speakQueue = []
  voiceState.isSpeaking = false
}

/**
 * 开启语音总开关（需要在用户click事件里调用一次，解锁浏览器播放权限）
 */
export async function openVoiceSwitch() {
  if (!synth) throw new Error('浏览器不支持语音播报')
  voiceState.enableVoice = true
  localStorage.setItem('voiceSwitch', 'true')
  // 触发一次空播报，解决部分WebView语音不激活问题
  synth.cancel()
}

/**
 * 关闭语音总开关，同时停止当前播报、清空队列
 */
export function closeVoiceSwitch() {
  voiceState.enableVoice = false
  localStorage.setItem('voiceSwitch', 'false')
  stopAllSpeak()
}

/**
 * 检测是否支持语音能力
 */
export function isSupportSpeech() {
  return !!synth
}
